import { useEffect, useState } from "react"
import { useThemeStore } from "shared-lib"
import { Theme, ThemeProviderContext } from "./theme_provider_context"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

export function ShadCNThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    // Seed from the app-wide theme store (the antd ConfigProvider and
    // ThemeToggle use it) so the `.dark` class this provider puts on <html>
    // — which drives Tailwind's `dark:` variants and the scrollbar colours in
    // index.css — matches the antd algorithm from the first paint. It used to
    // read only its own `vite-ui-theme` key, which ThemeToggle never writes:
    // with a stored light theme the page still started with `.dark` set.
    () =>
      useThemeStore.getState().theme ??
      (localStorage.getItem(storageKey) as Theme | null) ??
      defaultTheme
  )

  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove("light", "dark")

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light"

      root.classList.add(systemTheme)
      return
    }

    root.classList.add(theme)
  }, [theme])

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme)
      setTheme(theme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}
